import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListingViewTrend } from "@/components/listing-view-trend";
import { AdminPartnerListing } from "./types";

export function RealtorListingsPanel({ partnerId }: { partnerId: number }) {
  const listingsQuery = useQuery<AdminPartnerListing[]>({
    queryKey: ["/api/admin/partners", partnerId, "listings"],
    queryFn: () => fetch(`/api/admin/partners/${partnerId}/listings`, { credentials: "include" }).then(r => r.json()),
  });
  const listings = listingsQuery.data ?? [];
  const totalViews = listings.reduce((sum, l) => sum + (l.viewCount || 0), 0);
  const totalUniqueViewers = listings.reduce((sum, l) => sum + (l.uniqueViewers || 0), 0);
  return (
    <Card className="p-4 space-y-3" data-testid="card-realtor-listings">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Listings & View Counts</h3>
        <span className="text-xs text-muted-foreground" data-testid="text-realtor-total-views">
          Total views: <span className="font-medium text-foreground">{totalViews}</span>
          {" · "}
          <span data-testid="text-realtor-total-unique-viewers">
            Buyers: <span className="font-medium text-foreground">{totalUniqueViewers}</span>
          </span>
        </span>
      </div>
      {listingsQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : listings.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="text-no-realtor-listings">No listings submitted yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Address</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-right">Price</TableHead>
                <TableHead className="text-xs text-right">Views</TableHead>
                <TableHead className="text-xs text-right">Buyers</TableHead>
                <TableHead className="text-xs">Last 30 days</TableHead>
                <TableHead className="text-xs">Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listings.map((l) => (
                <TableRow key={l.id} data-testid={`row-realtor-listing-${l.id}`}>
                  <TableCell className="text-xs">
                    <div className="font-medium">{l.address}</div>
                    {l.city && l.state && (
                      <div className="text-muted-foreground">{l.city}, {l.state}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs capitalize">{l.status.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs text-right">${parseFloat(l.price || "0").toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-right font-medium" data-testid={`text-admin-listing-views-${l.id}`}>{l.viewCount ?? 0}</TableCell>
                  <TableCell className="text-xs text-right font-medium" data-testid={`text-admin-listing-unique-viewers-${l.id}`}>{l.uniqueViewers ?? 0}</TableCell>
                  <TableCell className="text-xs">
                    <div className="w-40">
                      <ListingViewTrend
                        listingId={l.id}
                        endpoint="admin"
                        days={30}
                        height={48}
                        testId={`chart-admin-listing-trend-${l.id}`}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(l.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
